import styles from '../provide-transactions/BankStatements.module.css';
import Text from '../../../../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../../../enums';
import screeningSteps, { Action } from '../../../../../organisms/screening-navigation/screeningSteps';
import { Card } from '../../../../../organisms/card';
import { Trans, useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import ScreeningNavigation from '../../../../../organisms/screening-navigation/ScreeningNavigation';
import Form, { Data, FormData, FormErrors } from '../../../../../molecules/form/Form';
import { FileUpload } from '../../../../../organisms/file-upload';
import Label from '../../../../../atoms/label/Label';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRoutePath } from '../../../../../utils/useRoutePath';
import submitBankStatements from '../../../../../../api/screening/income-verification/submit-bank-statements/submitBankStatements';
import Notifications from '../../../../../organisms/notifications/Notifications';
import findBankStatements, { FindBankStatements } from '../../../../../../api/screening/income-verification/find-bank-statements/findBankStatements';
import SkeletonComponent from '../../../../../../components/SkeletonComponent';
import DeleteFileConfirmationModal from '../../../../../organisms/delete-file-confirmation-modal/DeleteFileConfirmationModal';
import deleteBankStatement from '../../../../../../api/screening/income-verification/delete-bank-statement/deleteBankStatement';
import { FileMetaData } from '../../../../../organisms/file-upload/FileUpload';
import PageContentNotFound from '../../../../../organisms/page-content-not-found/PageContentNotFound';

const BankStatements = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const routePath = useRoutePath();
    const { homeseekerId } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ formData, setFormData ] = useState<Data>({});
    const [ formErrors, setFormErrors ] = useState<FormErrors>({});
    const [ isSubmittingBankStatements, setIsSubmittingBankStatements ] = useState<boolean>(false);
    const [ isSubmittingBankStatementsSuccess, setIsSubmittingBankStatementsSuccess ] = useState<boolean>(false);
    const [ isFetchingBankStatements, setIsFetchingBankStatements ] = useState<boolean>(true);
    const [ storedData, setStoredData ] = useState<FindBankStatements>({
        bankStatements: []
    });
    const [ bankStatementToDelete, setBankStatementToDelete ] = useState<string>(null);

    useEffect(() => {
        fetchBankStatements();
    }, []);

    const fetchBankStatements = async () => {
        try {
            const bankStatementsResponse = await findBankStatements(homeseekerId);
            setStoredData(bankStatementsResponse);
        } catch (error) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
            setStoredData(null);
        } finally {
            setIsFetchingBankStatements(false);
        }
    }

    const handleSubmit = async () => {
        setIsSubmittingBankStatements(true);
        setIsSubmittingBankStatementsSuccess(false);

        try {
            await submitBankStatements(homeseekerId, {
                bankStatements: formData.bankStatements?.value as FileList
            });

            navigate(routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId }));
        } catch (error) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsSubmittingBankStatements(false);
            setIsSubmittingBankStatementsSuccess(false);
        }
    }

    const handleBankStatementDelete = (key: string, name: string) => {
        if (storedData.bankStatements.find(file => file.name === name)) {
            setBankStatementToDelete(name);
            DeleteFileConfirmationModal.open(key, name, homeseekerId);
        }
    }

    const handleDeleteFileSuccess = () => {
        const documentKeyToDelete = bankStatementToDelete;

        setBankStatementToDelete(null);

        setStoredData({
            bankStatements: storedData.bankStatements.filter((file: FileMetaData) => file.name !== documentKeyToDelete)
        });
    }

    const handleFormChange = (data, _isValid, errors) => {
        setFormData(data);
        setFormErrors(errors);
    }

    const handleNavigationClick = (action: Action) => {
        if (action === 'previous') {
            navigate(routePath('ScreeningIncomeVerificationProvideTransactions', { homeseekerId }, { authorisationId }));
        }

        if (action === 'skip') {
            navigate(routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId }));
        }
    }

    if (!authorisationId) {
        return <PageContentNotFound/>;
    }

    if (isFetchingBankStatements) {
        return (
            <div className={styles.contentContainer}>
                <div className={styles.gridLg}>
                    <Text
                        color={ColorEnum.Primary700}
                        weight={FontWeightEnum.Bold}
                        size={FontSizeEnum.DisplayXs}
                    >{t(screeningSteps.ScreeningIncomeVerificationProvideIncomeDetails.pageTitleTranslationKey)}</Text>
                    <Card elevated>
                        <div className={styles.gridMd}>
                            <SkeletonComponent height={24}/>
                            <div className={styles.gridSm}>
                                <SkeletonComponent height={24}/>
                                <SkeletonComponent height={72}/>
                            </div>
                        </div>
                    </Card>
                    <ScreeningNavigation
                        isSubmitting={isSubmittingBankStatements}
                        isSuccessful={isSubmittingBankStatementsSuccess}
                        disabled
                        onClick={handleNavigationClick}
                    />
                </div>
            </div>
        )
    }

    return (
        <>
            <Form
                onSubmit={handleSubmit}
                onChange={handleFormChange}
                initialFormData={storedData as unknown as FormData}
            >
                <div className={styles.contentContainer}>
                    <div className={styles.gridLg}>
                        <Text
                            color={ColorEnum.Primary700}
                            weight={FontWeightEnum.Bold}
                            size={FontSizeEnum.DisplayXs}
                        >{t(screeningSteps.ScreeningIncomeVerificationProvideTransactions.pageTitleTranslationKey)}</Text>
                        <Card elevated>
                            <div className={styles.gridMd}>
                                <Text
                                    tag='p'
                                    color={ColorEnum.Gray800}
                                    weight={FontWeightEnum.Semibold}>
                                    {t('screening.incomeVerification.provideTransactions.bankStatements.label')}
                                </Text>
                                <Text>
                                    <Trans
                                        i18nKey="screening.incomeVerification.provideTransactions.bankStatements.supportingText"
                                        components={{
                                            strong: <strong/>
                                        }}
                                    />
                                </Text>
                                <div>
                                    <Label>{t('screening.incomeVerification.provideTransactions.bankStatements.upload.label')}</Label>
                                    <FileUpload
                                        name='bankStatements'
                                        storedFileMetadata={storedData?.bankStatements}
                                        acceptedExtensions={['pdf']}
                                        multiple
                                        errors={formErrors['bankStatements']}
                                        required
                                        onDelete={(name: string) => handleBankStatementDelete('bankStatements', name)}
                                    />
                                </div>
                            </div>
                        </Card>
                        <ScreeningNavigation
                            isSubmitting={isSubmittingBankStatements}
                            isSuccessful={isSubmittingBankStatementsSuccess}
                            onClick={handleNavigationClick}
                        />
                    </div>
                </div>
            </Form>
            <DeleteFileConfirmationModal
                deleteCallback={(_key: string, filename: string, id: string) => deleteBankStatement(filename, id)}
                onDeleteSuccess={handleDeleteFileSuccess}
            ></DeleteFileConfirmationModal>
        </>
    );
}

export default BankStatements;
